import { BubbleFormPage } from '@go-client/components/BubbleUI/BubbleForm';
import GroupBookingsGrid from '@go-client/components/GroupBookingsGrid';
import { isoDate } from '@go-shared/lib/time';
import {
  GroupBooking,
  VenueRequest,
  VenueRequestFile,
  VenueRequestStatus,
} from '@go-shared/types/groupBooking';
import { Property } from '@go-shared/types/property';
import { BareSimpleSailingDayBundle } from '@go-shared/types/sailing';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import React from 'react';
import EnterBookingPage from '../EnterBookingPage';
import {
  BookingForm,
  GroupBookingDataBundle,
  GroupBookingFormWorkflow,
  GroupForm,
  submitBookingForm,
} from '.';
import { User } from '@go-shared/types/user';
import SelectItineraryPage from '../SelectItineraryPage';
import BookingConfirmationPage from '../BookingConfirmationPage';
import { getHourFormat } from '@go-shared/lib/property';
import { DayMarkersButton } from '@go-client/components/DayMarkersButton';

interface EditBookingWorkflowPrereqs {
  dataBundle: GroupBookingDataBundle;
  selectedGroup: GroupBooking;
  selectedBooking: VenueRequest;
  selectedVenue: VenueTemplate;
  selectedSailingBundle?: BareSimpleSailingDayBundle;
  bookingForm: BookingForm;
  setBookingForm: React.Dispatch<React.SetStateAction<BookingForm>>;
  changeBookingFormAttribute: <T extends keyof BookingForm>(key: T, val: BookingForm[T]) => void;
  user: User;
  property: Property;
  isNewGroup: boolean;
  groupForm: GroupForm;
  showDayContext: boolean;
  setShowDayContext: React.Dispatch<React.SetStateAction<boolean>>;
  defaultStartDate?: string;
  defaultEndDate?: string;
  languages: string[];
  defaultLanguage: string;
  handleSelectSailingBundle: (sailingBundle: BareSimpleSailingDayBundle) => void;
  handleSelectVenue: (venue: VenueTemplate) => void;
  handleBookingUploadFile: (file: File) => void;
  handleBookingUpdateFile: (file: VenueRequestFile) => void;
  handleBookingDeleteFile: (file: VenueRequestFile) => void;
  onClose?: () => void;
  onSubmitBooking?: () => void;
}

export default function buildEditBookingWorkflow(
  pages: Array<BubbleFormPage<GroupBookingDataBundle>>,
  preqs: EditBookingWorkflowPrereqs
): GroupBookingFormWorkflow {
  const hourFormat = getHourFormat(preqs.property);

  // disabled for now: check if preqs.showDayContext is true
  const prependNode = preqs.selectedSailingBundle && preqs.selectedVenue && (
    <div className="context-view shadow-lg">
      <div className="p-3">
        <DayMarkersButton sailingDay={preqs.selectedSailingBundle.day} />
      </div>
      <div className="italic text-slate px-3">
        <span className="font-bold">TIP:</span> You can click and drag to set the day, start time,
        and end time of this booking!
      </div>
      <GroupBookingsGrid
        key={`${preqs.selectedSailingBundle.sailing._id}-${preqs.selectedVenue._id}`}
        languages={preqs.languages}
        defaultLanguage={preqs.defaultLanguage}
        hourFormat={hourFormat}
        bufferTime={preqs.bookingForm.bufferTime}
        groupBookingDate={isoDate(preqs.selectedSailingBundle.day.date)}
        groupBookingStartTime={preqs.bookingForm.startTime}
        groupBookingEndTime={preqs.bookingForm.endTime}
        days={preqs.selectedSailingBundle.sailing.days.length}
        sailing={preqs.selectedSailingBundle.sailing}
        venue={preqs.selectedVenue}
        omitted={[preqs.selectedBooking._id]}
        onStartTimeChange={(time, idx) => {
          preqs.changeBookingFormAttribute('startTime', time);

          if (preqs.selectedSailingBundle) {
            const newDay = preqs.selectedSailingBundle.sailing.days[idx];
            preqs.handleSelectSailingBundle({
              ...preqs.selectedSailingBundle,
              day: newDay,
              dayIndex: idx,
              dateIso: isoDate(newDay.date),
            });
          }
        }}
        onEndTimeChange={time => preqs.changeBookingFormAttribute('endTime', time)}
      />
    </div>
  );

  async function handleSubmitBookingForm(status?: VenueRequestStatus) {
    await submitBookingForm({
      user: preqs.user,
      property: preqs.property,
      type: 'edit',
      selectedGroup: preqs.selectedGroup,
      selectedVenue: preqs.selectedVenue,
      selectedSailingBundle: preqs.selectedSailingBundle,
      bookingToReplace: preqs.selectedBooking,
      bookingDetailsForm: preqs.bookingForm,
      status: status || 'pending',
      newGroup: false,
    });

    if (preqs.onSubmitBooking) preqs.onSubmitBooking();
    if (preqs.onClose) preqs.onClose();
  }

  pages.push({
    key: 'edit-page-1',
    render: actions => (
      <SelectItineraryPage
        pageIndex={1}
        sailings={preqs.dataBundle.simpleSailings}
        venueTemplates={preqs.dataBundle.venueTemplates}
        isNewGroup={preqs.isNewGroup}
        groupForm={preqs.groupForm}
        selectedGroup={preqs.selectedGroup}
        selectedSailingBundle={preqs.selectedSailingBundle}
        selectedVenue={preqs.selectedVenue}
        showDayContext={preqs.showDayContext}
        onSelectSailingBundle={preqs.handleSelectSailingBundle}
        handleOnSelectVenue={preqs.handleSelectVenue}
        onToggleShowDayContext={() => preqs.setShowDayContext(!preqs.showDayContext)}
        defaultStartDate={preqs.defaultStartDate}
        defaultEndDate={preqs.defaultEndDate}
        onGoNext={actions.goForward}
        onClose={preqs.onClose}
      />
    ),
  });

  pages.push({
    key: 'edit-page-2',
    render: actions => (
      <EnterBookingPage
        title="Edit Group Booking"
        pageIndex={2}
        bookingForm={preqs.bookingForm}
        changeBookingForm={preqs.changeBookingFormAttribute}
        groupResources={preqs.dataBundle.groupResources}
        isNewGroup={preqs.isNewGroup}
        groupForm={preqs.groupForm}
        selectedGroup={preqs.selectedGroup}
        selectedSailingBundle={preqs.selectedSailingBundle}
        selectedVenue={preqs.selectedVenue}
        venueConfigOpts={preqs.selectedVenue.configurations}
        showDayContext={preqs.showDayContext}
        hideDraft
        previousStatus={preqs.selectedBooking.status}
        submitTitle="Finish: Edit Group Booking"
        onUploadFile={preqs.handleBookingUploadFile}
        onDeleteFile={preqs.handleBookingDeleteFile}
        onUpdateFile={preqs.handleBookingUpdateFile}
        onClose={preqs.onClose}
        onGoBack={actions.goBack}
        onGoForward={actions.goForward}
        onToggleShowDayContext={() => preqs.setShowDayContext(!preqs.showDayContext)}
        onSubmitBookingForm={handleSubmitBookingForm}
      />
    ),
  });

  pages.push({
    key: 'edit-page-3',
    render: actions => (
      <BookingConfirmationPage
        title="Edit Group Booking"
        pageIndex={3}
        isNewGroup={preqs.isNewGroup}
        groupForm={preqs.groupForm}
        onClose={preqs.onClose}
        onGoStart={actions.goStart}
      />
    ),
  });

  return { prependNode };
}
